// TODO: Agregar tipos correctos
// ===========================================
// WORKSPACE: Contable
// Motor Fiscal y Financiero
// ===========================================

import { lazy } from 'react'
import {
  Calculator,
  Receipt,
  FileText,
  CalendarCheck,
  Briefcase,
  DollarSign,
  Inbox,
  Wallet,
  Banknote,
  FileCheck2,
} from 'lucide-react'
import type { Workspace, WorkspaceModule } from '@/core/types/workspace'

// ===========================================
// LAZY LOAD DE PAGINAS
// Mantiene las paginas existentes mientras migramos
// ===========================================

// Paginas existentes (se importan de /pages por ahora)
// Usamos .then() para convertir named exports a default exports
const Movimientos = lazy(() => import('@/pages/Movimientos').then(m => ({ default: m.Movimientos })))
const Cuentas = lazy(() => import('@/pages/Cuentas').then(m => ({ default: m.Cuentas })))
const Gastos = lazy(() => import('@/pages/Gastos').then(m => ({ default: m.Gastos })))
const Presupuestos = lazy(() => import('@/pages/Presupuestos').then(m => ({ default: m.Presupuestos })))
const Facturas = lazy(() => import('@/pages/Facturas').then(m => ({ default: m.Facturas })))
const Calendario = lazy(() => import('@/pages/Calendario').then(m => ({ default: m.Calendario })))
const Impuestos = lazy(() => import('@/pages/Impuestos')) // Página de impuestos
const Servicios = lazy(() => import('@/pages/Servicios').then(m => ({ default: m.Servicios })))
const Reportes = lazy(() => import('@/pages/Reportes').then(m => ({ default: m.Reportes })))

// Pagina AFIP - Casillero Electronico
const CasilleroElectronico = lazy(() => import('@/pages/CasilleroElectronico').then(m => ({ default: m.CasilleroElectronico })))

// ===========================================
// MODULOS DEL WORKSPACE CONTABLE
// ===========================================

const MODULO_HONORARIOS: WorkspaceModule = {
  id: 'honorarios',
  nombre: 'Honorarios del Estudio',
  descripcion: 'Gestion de abonos, movimientos y cuenta corriente de clientes',

  // NOTA: este array `routes` NO alimenta el router (0 consumidores). Las rutas reales
  // se registran a mano en src/router.tsx. Toda ruta nueva va ahí + export en @/pages.
  // El test src/router.test.ts verifica que cada nav tenga su ruta. Ver tasks/lessons.md (2026-06-01).
  routes: [
    { path: '/movimientos', component: Movimientos, permission: 'verMovimientos' },
    { path: '/cuentas', component: Cuentas, permission: 'verCuentas' }, // Ruta activa pero oculta del nav
    { path: '/gastos', component: Gastos, permission: 'verCuentas' },
    { path: '/presupuestos', component: Presupuestos, permission: 'verPresupuestos' },
    { path: '/facturas', component: Facturas, permission: 'verFacturas' },
  ],

  navigation: [
    { name: 'Movimientos', href: '/movimientos', icon: Receipt, permission: 'verMovimientos' },
    // Cuentas: reactivado solo para contador_principal (ORR-121); el sidebar filtra por permission
    { name: 'Cuentas', href: '/cuentas', icon: Wallet, permission: 'verCuentas' },
    { name: 'Gastos', href: '/gastos', icon: Banknote, permission: 'verCuentas' },
    // ORR-192: Presupuestos se agrupa en "Ventas" (no en "Finanzas" con el resto del módulo)
    { name: 'Presupuestos', href: '/presupuestos', icon: FileText, permission: 'verPresupuestos', group: 'ventas' },
    // ORR-255: facturación electrónica, junto a Presupuestos en "Ventas"
    { name: 'Facturas', href: '/facturas', icon: FileCheck2, permission: 'verFacturas', group: 'ventas' },
  ],

  permissions: [
    'verMovimientos',
    'crearMovimiento',
    'editarMovimiento',
    'eliminarMovimiento',
    'generarLoteMovimientos',
    'verCuentas',
    'crearCuenta',
    'editarCuenta',
    'crearGasto',
    'editarGasto',
    'eliminarGasto',
    'gestionarCategoriasGasto',
    'verPresupuestos',
    'crearPresupuesto',
    'editarPresupuesto',
  ],

  defaultEnabled: true,
}

const MODULO_IMPUESTOS: WorkspaceModule = {
  id: 'impuestos',
  nombre: 'Impuestos',
  descripcion: 'Vencimientos, calendario fiscal, declaraciones juradas',

  routes: [
    { path: '/calendario', component: Calendario, permission: 'verVencimientos' },
    { path: '/impuestos', component: Impuestos, permission: 'verImpuestos' },
  ],

  navigation: [
    { name: 'Calendario', href: '/calendario', icon: CalendarCheck, permission: 'verVencimientos' },
    { name: 'Impuestos', href: '/impuestos', icon: DollarSign, permission: 'verImpuestos' },
  ],

  permissions: [
    'verImpuestos',
    'crearImpuesto',
    'editarImpuesto',
    'registrarPagoImpuesto',
    'verVencimientos',
    'crearVencimiento',
    'editarVencimiento',
    'marcarPresentado',
  ],

  dependencies: ['documentacion_fiscal'],
  defaultEnabled: true,
}

const MODULO_AFIP: WorkspaceModule = {
  id: 'afip',
  nombre: 'AFIP / ARCA',
  descripcion: 'Integración directa con servicios web de AFIP/ARCA',

  routes: [
    { path: '/fiscal/casillero', component: CasilleroElectronico, permission: 'verCasilleroElectronico' },
  ],

  navigation: [
    { name: 'Casillero Electrónico', href: '/fiscal/casillero', icon: Inbox, permission: 'verCasilleroElectronico' },
  ],

  permissions: [
    'verCasilleroElectronico',
    'abrirComunicacionCasillero',
  ],

  defaultEnabled: true,
}

const MODULO_SERVICIOS: WorkspaceModule = {
  id: 'servicios',
  nombre: 'Servicios',
  descripcion: 'Catalogo de servicios del estudio',

  routes: [
    { path: '/servicios', component: Servicios, permission: 'verClientes' },
  ],

  navigation: [
    { name: 'Servicios', href: '/servicios', icon: Briefcase, permission: 'verClientes' },
  ],

  permissions: [],
  defaultEnabled: true,
}

const MODULO_REPORTES: WorkspaceModule = {
  id: 'reportes',
  nombre: 'Reportes',
  descripcion: 'Reportes de deudas, ingresos y analisis',

  routes: [
    { path: '/reportes', component: Reportes, permission: 'verReportes' },
  ],

  // ORR-192: Reportes se renderiza vía ADMIN_NAVIGATION (sección Administración), no por el
  // workspace. El módulo se mantiene por su ruta (/reportes en router.tsx) y sus permisos.
  navigation: [],

  permissions: ['verReportes', 'exportarReportes'],
  defaultEnabled: true,
}

// ===========================================
// DEFINICION DEL WORKSPACE
// ===========================================

export const WORKSPACE_CONTABLE: Workspace = {
  id: 'contable',
  nombre: 'Contable',
  descripcion: 'Motor fiscal y financiero',
  icono: Calculator,
  color: '#10B981', // Verde esmeralda

  facetaCliente: 'cliente_faceta_contable',

  modulos: [
    MODULO_HONORARIOS,
    MODULO_AFIP,
    MODULO_SERVICIOS,
    MODULO_IMPUESTOS,
    MODULO_REPORTES,
  ],

  // ORR-192: reagrupado. Presupuestos entra a 'ventas' vía override group (el módulo
  // honorarios sigue mapeado a 'finanzas'). Reportes salió del nav del workspace.
  navigationGroups: [
    {
      id: 'ventas',
      label: 'Ventas',
      items: ['servicios'],
    },
    {
      id: 'finanzas',
      label: 'Finanzas',
      items: ['honorarios'],
    },
    {
      id: 'fiscal',
      label: 'Fiscal',
      items: ['afip', 'impuestos'],
    },
  ],

  dashboardWidgets: [
    'stats-resumen',
    'proximos-vencimientos',
    'clientes-deudores',
    'tareas-pendientes',
    'novedades-afip',
    'ingresos-mensuales',
  ],
}

// ===========================================
// EXPORTACIONES ADICIONALES
// ===========================================

export { MODULO_HONORARIOS, MODULO_AFIP, MODULO_IMPUESTOS, MODULO_REPORTES }
